import { SyntheticEvent, useCallback } from "react";
import classNames from "classnames";

import { ColorName, ServiceIcon, Text } from "@zapier/design-system";
import { IconName } from "@zapier/zinnia-icons";
import {
  NodeType,
  DefaultNodeData,
  ZapMetadata,
  floatingToolbarActiveButtonAtom,
  serviceAtomFamily,
  ZapNodeData,
  determineAssetType,
  saveCanvasSetter,
  toggleZapStepNodesAndEdgesSetterAtomFamily,
  nodesAtom,
  hasZslAtom,
  getAssetId,
  assetAccessAtomFamily_SUSPENDS,
  isDraftOrTemplateNodeAtomFamily,
  isDraftNotTemplateNodeAtomFamily,
  capitalizedAssetTypeDisplayAtom,
  isEmbedAtom,
} from "@zapier/canvas-state";
import { useAtomValue, useAtom, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { NodeProps, Node } from "@xyflow/react";
import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";
import { copilotLoadingNodeIdAtom } from "@zapier/copilot-tools";

import { StepTitle } from "../../../../components/StepTitle";
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter";
import { getDefaultColorForAssetType } from "../../../../utils/getDefaultColorForAssetType";
import { usePreviewContext } from "../../../../context/PreviewProvider";
import { useAssetTitleSync } from "../../../../hooks/useAssetTitleSync";

import { UnstyledButton } from "../../../UnstyledButton";
import { AssetAccessChecker } from "../../../AssetAccessChecker";
import { ToggleZapSteps } from "../../../ToggleZapSteps";
import { AssetLink } from "../../../AssetLink";
import { NodeLinkDisplay } from "../../../NodeLinkDisplay";
import { ActorLabelPositioner } from "../../../ActorLabelPositioner";

import { AssetPreview } from "./AssetPreview";
import { getContrastingTextColor } from "./utils";
import { NotesButton } from "./NotesButton";
import { AgentBotAvatar } from "./AgentBotAvatar";
import { AssetIcon } from "./AssetIcon";
import { AssetNotFound } from "./AssetNotFound";
import { EmbedContentThumbnail } from "../../../Embeds";
import styles from "./AssetNodeSkeleton.module.css";

type Props = {
  className: string;
  placeholder?: string;
  zap?: ZapMetadata;
  isZapExpanded?: boolean;
  isZapNotFound?: boolean;
  isViewOnlyMode?: boolean;
  iconName?: IconName;
  backgroundColor?: string;
  onMouseEnter?: () => void;
};

export function AssetNodeSkeleton(
  props: NodeProps<Node<DefaultNodeData | ZapNodeData>> & Props,
) {
  const getAssetDisplayName = useAtomValue(capitalizedAssetTypeDisplayAtom);

  const { isPreview } = usePreviewContext();
  const {
    id,
    data,
    type,
    selected,
    className,
    isViewOnlyMode = false,
    iconName,
    zap,
    backgroundColor,
    isZapNotFound,
    onMouseEnter,
  } = props;
  const hasNotes = !isRemirrorJSONEmpty(data.notesJson);

  const assetType = determineAssetType(
    (data as DefaultNodeData).services,
    type as NodeType,
  );
  const assetId = getAssetId(data, assetType);
  const isAssetUnlinked = Boolean(assetType && !assetId);
  const isDraftNotTemplateNode = useAtomValue(
    isDraftNotTemplateNodeAtomFamily(id),
  );
  const isGhostNode = isAssetUnlinked || isDraftNotTemplateNode;

  const isDraftOrTemplateNode = useAtomValue(
    isDraftOrTemplateNodeAtomFamily(id),
  );

  const {
    link,
    displayLinkAs,
    color = getDefaultColorForAssetType({
      assetType,
      assetId,
      isDraftNotTemplateNode: isDraftNotTemplateNode,
    }),
    label,
    tableId,
    projectId,
    chatbotId,
    agentId,
    actors,
    proposalId,
  } = data as DefaultNodeData;

  const { zapId, isExpanded } = data as ZapNodeData;
  const actor = actors?.[0];
  const hasZsl = useAtomValue(hasZslAtom);
  const showEmbedContent = Boolean(link && displayLinkAs === "embed");

  const isEmbed = useAtomValue(isEmbedAtom);

  // Need hasZsl or else the data we need might not be there when you click the button
  // Need !isPreview or else the button shows up in the ZapPreview and does nothing
  // Need assetType !== "chatbot" or else the button shows up for chatbot nodes
  const showAssetDetailsButtonInDraft =
    Boolean(hasZsl || proposalId) &&
    isDraftOrTemplateNode &&
    !isPreview &&
    assetType !== "chatbot";

  const { isAssetNotFound, isAssetInaccessible } = useAtomValue(
    assetAccessAtomFamily_SUSPENDS({
      assetId,
      interfaceProjectId: projectId,
      assetType,
    }),
  );

  // Hide Focus mode in embeds until we fix the alignment issue of the nested iframe.
  // See: https://cdn.zappy.app/f93e25ccb4b9641b1f54efd1d5dfe2a7.gif
  const showAssetDetailsButtonInBuildMode =
    !isDraftOrTemplateNode &&
    !isZapNotFound &&
    !isAssetInaccessible &&
    !isEmbed;

  const appId =
    (data as DefaultNodeData).services?.[0]?.currentImplementation?.id ?? "";

  const [activeButton, setActiveButton] = useAtom(
    floatingToolbarActiveButtonAtom,
  );
  const app = useAtomValue(serviceAtomFamily(appId));

  const isAgentNodeWithAgentId = assetType === "agent" && assetId;
  const isBlankAgentNode = assetType === "agent" && !assetId;

  const toggleFloatingLinkInput = useCallback(() => {
    const isAlreadyOpen = "link" === activeButton;
    setActiveButton(isAlreadyOpen ? null : "link");
  }, [activeButton, setActiveButton]);

  // Sync asset titles with step title
  const zapTitle = zap?.title ?? "";
  useAssetTitleSync({
    nodeData: data,
    nodeType: type as string,
    label,
    zapTitle,
  });

  const maybeCapitalizedAssetType =
    assetType === "zap"
      ? getAssetDisplayName(assetType)
      : getAssetDisplayName(assetType).toLowerCase();

  const zapSteps = zap?.steps || [];
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const toggleZapStepsAndEdges = useSetAtom(
    toggleZapStepNodesAndEdgesSetterAtomFamily(id),
  );
  const loadingNodeId = useAtomValue(copilotLoadingNodeIdAtom);
  const isCopilotWorkingOnNode = loadingNodeId === id;

  /** Persist whether Zap node is expanded, so we can show the same UI on page load.
   * React Flow state updates are handled by toggleZapStepsAndEdges */
  const saveZapNodeToBE = useAtomCallback(
    useCallback(
      async (get: (atom: typeof nodesAtom) => Node[]) => {
        const nodes = get(nodesAtom);
        const updatedNodes = nodes.map((node: Node) => {
          const isZapNode = node.id === id;

          if (!isZapNode) {
            return node;
          }

          return {
            ...node,
            data: {
              ...node.data,
              isExpanded: !isExpanded,
            },
          };
        });

        saveToBackend({ nodes: updatedNodes });
      },
      [id, isExpanded, saveToBackend],
    ),
  );

  const onToggleZapSteps = useCallback(
    (event: SyntheticEvent<HTMLButtonElement | HTMLUListElement>) => {
      /** To prevent the floating toolbar from opening */
      event.stopPropagation();
      toggleZapStepsAndEdges(Boolean(isExpanded));
      saveZapNodeToBE();
    },
    [isExpanded, saveZapNodeToBE, toggleZapStepsAndEdges],
  );

  if (isAssetNotFound) {
    return (
      <AssetNotFound
        id={id}
        data={data}
        type={type}
        selected={selected}
        iconName={iconName}
      />
    );
  }

  return (
    <div
      className={classNames(
        styles.container,
        styles[assetType || "default"],
        {
          [styles.elevated]: isCopilotWorkingOnNode,
          [styles.unlinkedContainer]: isGhostNode,
        },
        className,
      )}
      style={{
        backgroundColor: color,
      }}
      onMouseEnter={onMouseEnter}
    >
      {(zapId || tableId || projectId || chatbotId || agentId) && (
        <AssetAccessChecker
          assetId={assetId}
          interfaceProjectId={projectId}
          assetType={assetType}
        />
      )}
      <div className={styles.bodyContainer}>
        <div
          className={classNames({
            [styles.whiteBackground]: !isGhostNode,
          })}
        >
          <div
            className={styles.body}
            style={{
              backgroundColor,
            }}
          >
            {actor && !isAgentNodeWithAgentId && (
              <ActorLabelPositioner
                actor={actor}
                shouldOverlap={
                  assetType !== "agent"
                } /* Don't overlap actor label for agent nodes as it blocks the step title */
              />
            )}
            {/* hide in ZapPreview since the ToggleZapSteps doesn't work  */}
            {!isPreview && zapSteps && zapSteps.length > 0 && (
              <div className={styles.toggleZapStepsWrapper}>
                <ToggleZapSteps
                  onClick={onToggleZapSteps}
                  isExpanded={Boolean(isExpanded)}
                  steps={zapSteps}
                />
              </div>
            )}
            <div
              className={classNames(styles.previewImage, {
                [styles.unlinkedPreviewImage]: isGhostNode,
              })}
              style={
                !assetType ? { backgroundColor: `#${app?.primary_color}` } : {}
              }
            >
              {showEmbedContent && <EmbedContentThumbnail url={link!} />}
              {assetType &&
                (assetType !== "agent" || isAgentNodeWithAgentId) && (
                  <AssetPreview
                    id={id}
                    assetType={assetType}
                    assetId={assetId}
                    showAssetDetailsButton={
                      showAssetDetailsButtonInDraft ||
                      showAssetDetailsButtonInBuildMode
                    }
                    projectId={projectId}
                  />
                )}
              {!showEmbedContent && app?.images?.url_128x128 && !assetType && (
                <div className={styles.serviceIconWrapper}>
                  <ServiceIcon
                    serviceName={app?.name}
                    size="large"
                    src={app?.images?.url_128x128}
                  />
                </div>
              )}
            </div>
            {!isAgentNodeWithAgentId && (
              <StepTitle
                nodeId={id}
                label={data.label}
                isSelected={Boolean(selected)}
                placeholder={props.placeholder}
                canNodeGetAiSuggestion={false}
                rows={2}
                isViewOnlyMode={isViewOnlyMode || isPreview}
                isBlankAgentNode={isBlankAgentNode}
                shouldHaveCharLimit={assetType !== "agent"}
              />
            )}
          </div>
        </div>
      </div>
      <div
        className={classNames(styles.footer, {
          [styles.unlinkedFooter]: isGhostNode,
        })}
      >
        {isAgentNodeWithAgentId && !isDraftOrTemplateNode ? (
          <AgentBotAvatar agentId={assetId!} />
        ) : (
          iconName && <AssetIcon iconName={iconName} />
        )}
        {app?.images?.url_16x16 && !assetType && (
          <ServiceIcon
            serviceName={app?.name}
            size="small"
            src={app?.images?.url_16x16}
          />
        )}
        {!link && (
          <Text
            type="MinimalPrint1"
            color={getContrastingTextColor(color!) as ColorName}
          >
            <div
              className={classNames(styles.truncateText, {
                [styles.truncateTextNoNotes]: !hasNotes,
              })}
            >
              {getAssetDisplayName(assetType) ||
                capitalizeFirstLetter(app?.name ?? "")}
            </div>
          </Text>
        )}
        {link && (
          <NodeLinkDisplay
            link={link}
            isViewOnlyMode={isViewOnlyMode}
            onLinkClick={toggleFloatingLinkInput}
            textColor={getContrastingTextColor(color!) as ColorName}
            linkColor={getContrastingTextColor(color!, true)}
          />
        )}
        {!isDraftOrTemplateNode && assetId && (
          <AssetLink
            assetId={assetId}
            assetName={maybeCapitalizedAssetType}
            interfaceProjectId={projectId}
            color={color}
          />
        )}
        {isAssetUnlinked && (
          <UnstyledButton
            onClick={toggleFloatingLinkInput}
            disabled={isViewOnlyMode}
          >
            <Text
              type="MinimalPrint1"
              color={getContrastingTextColor(color!) as ColorName}
              className={styles.underline}
            >
              Link existing
            </Text>
          </UnstyledButton>
        )}
        <NotesButton
          id={id}
          hasNotes={hasNotes}
          isViewOnlyMode={isViewOnlyMode}
        />
      </div>
    </div>
  );
}
